// User Data Persistence - Facade for backward compatibility
// Delegates to modular storage services while maintaining original API

// Storage Detection & Environment
export {
  shouldDisableEncryption,
  detectStorageEnvironment,
  getStorageAvailability,
  clearStorageAvailabilityCache,
} from './storage/storageDetection';

// Storage Helpers & Core Operations
export {
  USER_DATA_KEY,
  localStorageHelpers,
  sessionStorageHelpers,
  cookieHelpers,
  urlParamHelpers,
  inMemoryHelpers,
  readFromStorage,
  writeToStorage,
  removeFromStorage,
} from './storage/storageHelpers';

// Encryption Service
export {
  getEncryptionKey,
  encryptValue,
  decryptValue,
  clearDecryptionCache,
  getEncryptionStrategy,
} from './storage/encryptionService';

// User Data CRUD Operations
export {
  storeUserData,
  getUserData,
  clearUserData,
  removeUserDataFields,
  getHashedUserData,
  clearPerformanceCache,
} from './storage/userDataService';

// Purchase State Management
export {
  getPurchaseState,
  updatePurchaseState,
  clearOTOPurchaseState,
  clearAllPurchaseStateExceptEmail,
  getPurchaseSummary,
  markInitialPurchaseCompleted,
  getPurchaseStateWithExpiringFields,
} from './storage/purchaseStateService';

// Storage Utils & Diagnostics
export {
  getStorageDiagnostics,
  clearStorageKeys,
  emergencyDisableEncryption,
  emergencyEnableEncryption,
  reportEncryptionStrategy,
  clearAllEncryptionKeys,
  getStorageSystemStatus,
} from './storage/storageUtils';

// Cache Management
export { clearAllCaches } from './storage/index';

// Analytics Queue Management
export { clearAnalyticsQueues } from '../components/analytics/queueManagement';

/**
 * Synchronously get a value from available sources
 * Priority: URL params → localStorage → sessionStorage
 * @param {string} key - The key to retrieve
 * @returns {string} The value if found, empty string otherwise
 */
export const getSync = (key) => {
  if (typeof window === 'undefined') return '';
  
  try {
    // 1. Check URL parameters first (highest priority)
    const urlParams = new URLSearchParams(window.location.search);
    const urlValue = urlParams.get(key);
    if (urlValue) {
      return urlValue;
    }
    
    // 2. Check localStorage (most reliable storage)
    const storageKey = 'swts_user_data';
    const localData = localStorage.getItem(storageKey);
    if (localData) {
      try {
        const parsed = JSON.parse(localData);
        if (parsed[key]) {
          return parsed[key];
        }
      } catch (e) {
        // Invalid JSON, continue to next source
      }
    }
    
    // 3. Check sessionStorage (fallback)
    const sessionData = sessionStorage.getItem(storageKey);
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed[key]) {
          return parsed[key];
        }
      } catch (e) {
        // Invalid JSON, continue
      }
    }
  } catch (error) {
    // Storage access might fail in some browsers
    console.error(`Error in getSync('${key}'):`, error);
  }
  
  return '';
};

/**
 * Synchronously get email from available sources with field-specific handling
 * @returns {string} The email if found (trimmed and lowercase), empty string otherwise
 */
export const getEmailSync = () => {
  const email = getSync('email');
  // Apply email-specific transformations
  return email ? email.trim().toLowerCase() : '';
};

/**
 * Complete system reset - clears everything and all encryption keys
 */
export const emergencySystemReset = () => {
  try {
    import('./storage/storageHelpers').then(
      ({ removeFromStorage, USER_DATA_KEY }) => {
        removeFromStorage(USER_DATA_KEY);
      }
    );
    import('./storage/storageUtils').then(({ clearAllEncryptionKeys }) => {
      clearAllEncryptionKeys();
    });
    import('./storage/encryptionService').then(({ clearDecryptionCache }) => {
      clearDecryptionCache();
    });

    console.warn(
      '🚨 EMERGENCY: Complete system reset performed. All data cleared.'
    );
  } catch (error) {
    console.error('Failed to perform emergency system reset:', error);
  }
};

// Default export for backward compatibility
const userDataPersistence = {
  shouldDisableEncryption: () =>
    import('./storage/storageDetection').then(m => m.shouldDisableEncryption()),
  detectStorageEnvironment: () =>
    import('./storage/storageDetection').then(m =>
      m.detectStorageEnvironment()
    ),
  getStorageAvailability: () =>
    import('./storage/storageDetection').then(m => m.getStorageAvailability()),
  storeUserData: data =>
    import('./storage/userDataService').then(m => m.storeUserData(data)),
  getUserData: params =>
    import('./storage/userDataService').then(m => m.getUserData(params)),
  clearUserData: () =>
    import('./storage/userDataService').then(m => m.clearUserData()),
  getPurchaseState: () =>
    import('./storage/purchaseStateService').then(m => m.getPurchaseState()),
  updatePurchaseState: updates =>
    import('./storage/purchaseStateService').then(m =>
      m.updatePurchaseState(updates)
    ),
  getStorageDiagnostics: () =>
    import('./storage/storageUtils').then(m => m.getStorageDiagnostics()),
  emergencyDisableEncryption: () =>
    import('./storage/storageUtils').then(m => m.emergencyDisableEncryption()),
};

export default userDataPersistence;
